import { useState } from "react";
import { IndianRupee, Info } from "lucide-react";
import { SellPhoneWhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { useSeo } from "@/lib/seo";

const Field = ({ label, as = "input", options, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium">{label}</span>
    {as === "select" ? (
      <select {...props} className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-[hsl(var(--ink))]">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : as === "textarea" ? (
      <textarea {...props} rows={3} className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-[hsl(var(--ink))]" />
    ) : (
      <input {...props} className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-[hsl(var(--ink))]" />
    )}
  </label>
);

export default function SellYourPhone() {
  useSeo({
    title: "Sell Your Phone in Rohini — Mobile Specialist",
    description: "Sell your old or used phone at Mobile Specialist, Sector 3 Rohini. Share your device details and get a fair valuation over WhatsApp.",
    path: "/sell-your-phone",
  });
  const [d, setD] = useState({ brand: "", model: "", storage: "", condition: "", batteryHealth: "", age: "", bill: "", accessories: "", description: "", name: "", phone: "" });
  const set = (k) => (e) => setD((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="container-wide py-12" data-testid="sell-page">
      <header className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">Sell Your Phone</p>
        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Turn your old phone into cash.</h1>
        <p className="mt-4 text-[hsl(var(--ink-soft))]">Fill in your device details below and we'll send you an approximate valuation over WhatsApp.</p>
      </header>

      <div className="mt-6 flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm text-[hsl(var(--ink-soft))]">
        <Info className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
        We don't auto-calculate prices online. Share the details and our team confirms a fair value on WhatsApp.
      </div>

      <form className="mt-8 grid gap-5 rounded-3xl border border-border bg-card p-6 sm:p-8 lg:grid-cols-2" onSubmit={(e) => e.preventDefault()} data-testid="sell-form">
        <Field label="Brand" placeholder="e.g. Apple" value={d.brand} onChange={set("brand")} data-testid="sell-brand" />
        <Field label="Model" placeholder="e.g. iPhone 14" value={d.model} onChange={set("model")} data-testid="sell-model" />
        <Field label="Storage" as="select" options={["64GB", "128GB", "256GB", "512GB"]} value={d.storage} onChange={set("storage")} data-testid="sell-storage" />
        <Field label="Condition" as="select" options={["Like New", "Excellent", "Good", "Fair"]} value={d.condition} onChange={set("condition")} data-testid="sell-condition" />
        <Field label="Battery Health" placeholder="e.g. 88%" value={d.batteryHealth} onChange={set("batteryHealth")} data-testid="sell-battery" />
        <Field label="Phone Age" as="select" options={["Under 6 months", "6–12 months", "1–2 years", "2+ years"]} value={d.age} onChange={set("age")} data-testid="sell-age" />
        <Field label="Original Bill" as="select" options={["Available", "Not available"]} value={d.bill} onChange={set("bill")} data-testid="sell-bill" />
        <Field label="Accessories" placeholder="e.g. Box, charger" value={d.accessories} onChange={set("accessories")} data-testid="sell-accessories" />
        <div className="lg:col-span-2"><Field label="Description (optional)" as="textarea" placeholder="Anything else we should know? Photos can be shared on WhatsApp." value={d.description} onChange={set("description")} data-testid="sell-description" /></div>
        <Field label="Your Name" placeholder="Name" value={d.name} onChange={set("name")} data-testid="sell-name" />
        <Field label="Phone Number" placeholder="Mobile number" value={d.phone} onChange={set("phone")} data-testid="sell-phone" />
        <div className="lg:col-span-2">
          <SellPhoneWhatsAppButton data={d} label="Get a Quote on WhatsApp" full testId="sell-submit" />
          <p className="mt-3 flex items-center gap-1.5 text-xs text-[hsl(var(--ink-soft))]"><IndianRupee className="h-3.5 w-3.5" /> Photos of your device can be shared directly in the WhatsApp chat.</p>
        </div>
      </form>
    </div>
  );
}
